import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import type { EdgeInsets } from 'react-native-safe-area-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ZoomCarousel } from '@/features/media/components/image/ZoomCarousel';
import { ZoomImage } from '@/features/media/components/image/ZoomImage';
import { toStringArray } from '@/features/media/utils/mediaUrls';
import type {
  AppNativeStackScreenProps,
  AppRoute
} from '@/navigation/types/navigation';
import type { CarouselLayoutSize } from '@/shared/types/carousel';
import { hasItems } from '@/shared/utils/array';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme, insets: EdgeInsets) =>
  StyleSheet.create({
    screen: {
      flex: 1,
      backgroundColor: theme.colors.surfaceContainerLow,
      paddingBottom: insets.bottom
    }
  });

type Props = AppNativeStackScreenProps<typeof AppRoute.Gallery>;

export const GalleryScreen: FC<Props> = ({ route }) => {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(theme, insets), [insets, theme]);

  const { position, mediaUrls } = route.params ?? {};
  const media = toStringArray(mediaUrls) ?? [];

  const renderItem = useCallback(
    (item: string, layout: CarouselLayoutSize) => (
      <ZoomImage
        uri={item}
        containerWidth={layout.width}
        containerHeight={layout.height}
      />
    ),
    []
  );

  return (
    <View style={styles.screen}>
      {hasItems(media) && (
        <ZoomCarousel
          data={media}
          renderItem={renderItem}
          initialIndex={position ?? 0}
          maxScale={4}
        />
      )}
    </View>
  );
};
